import PublicSiteLayout from "@/components/landing/PublicSiteLayout";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const sectionOrder = [
  "intro",
  "consentCollection",
  "optInPractice",
  "customerDisclosure",
  "messageTypes",
  "messageControls",
  "noPromotionalMessaging",
  "privacyTerms",
] as const;

type SectionKey = (typeof sectionOrder)[number];

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
}

export default function SmsConsent() {
  const { t } = useTranslation();

  const sections = sectionOrder.map((key) => ({
    key,
    title: t(`smsConsentPage.sections.${key}.title`),
    paragraphs: asStringArray(
      t(`smsConsentPage.sections.${key}.paragraphs`, {
        returnObjects: true,
      }),
    ),
    bullets: asStringArray(
      t(`smsConsentPage.sections.${key}.bullets`, {
        returnObjects: true,
      }),
    ),
  }));

  return (
    <PublicSiteLayout mainClassName="bg-[linear-gradient(180deg,#F7FAFF_0%,#FFFFFF_18%,#FFFFFF_100%)] py-12 lg:py-16">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mx-auto max-w-4xl overflow-hidden rounded-[1.75rem] border border-[#D8E3F4] bg-card shadow-[0_32px_90px_-54px_rgba(12,31,79,0.3)]">
          <div className="border-b border-[#E6EDF8] bg-[linear-gradient(135deg,#0C1F4F_0%,#14306F_100%)] px-6 py-8 lg:px-10">
            <p className="text-sm font-medium text-accent">
              {t("smsConsentPage.eyebrow")}
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white lg:text-4xl">
              {t("smsConsentPage.title")}
            </h1>
            <div className="mt-5 text-sm text-white/80">
              <p>{t("smsConsentPage.lastUpdated")}</p>
            </div>
          </div>

          <div className="px-6 py-8 lg:px-10 lg:py-10">
            <div className="space-y-10">
              {sections.map((section, index) => (
                <section key={section.key} className="space-y-4">
                  <h2 className="text-xl font-bold text-primary lg:text-2xl">
                    {index + 1}) {section.title}
                  </h2>

                  {section.key !== "privacyTerms"
                    ? section.paragraphs?.map((paragraph) => (
                        <p key={paragraph} className="text-sm leading-7 text-muted-foreground">
                          {paragraph}
                        </p>
                      ))
                    : null}

                  {section.key === "customerDisclosure" ? (
                    <div className="rounded-2xl border border-[#F6D9A6] bg-[#FFF8E8] px-5 py-5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#A86407]">
                        {t("smsConsentPage.sampleDisclosureLabel")}
                      </p>
                      <blockquote className="mt-3 text-sm leading-7 text-[#5C3B03]">
                        {t("smsConsentPage.sampleDisclosure")}
                      </blockquote>
                    </div>
                  ) : null}

                  {section.bullets?.length && section.key === "optInPractice" ? (
                    <ol className="space-y-3">
                      {section.bullets.map((bullet, bulletIndex) => (
                        <li
                          key={bullet}
                          className="rounded-2xl border border-border/70 bg-[#FAFCFF] px-5 py-4 text-sm leading-7 text-muted-foreground"
                        >
                          <span className="mr-2 font-semibold text-primary">
                            {bulletIndex + 1}.
                          </span>
                          {bullet}
                        </li>
                      ))}
                    </ol>
                  ) : null}

                  {section.bullets?.length && section.key !== "optInPractice" ? (
                    <ul className="space-y-2 pl-5 text-sm leading-7 text-muted-foreground">
                      {section.bullets.map((bullet) => (
                        <li key={bullet} className="list-disc">
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {section.key === "privacyTerms" ? (
                    <div className="rounded-2xl border border-border/70 bg-[#FAFCFF] px-5 py-4">
                      <p className="text-sm leading-7 text-muted-foreground">
                        {section.paragraphs[0]}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-3 text-sm">
                        <Link className="font-medium text-accent hover:underline" to="/privacy">
                          {t("smsConsentPage.privacyLinkLabel")}
                        </Link>
                        <span className="text-muted-foreground">•</span>
                        <Link className="font-medium text-accent hover:underline" to="/terms">
                          {t("smsConsentPage.termsLinkLabel")}
                        </Link>
                        <span className="text-muted-foreground">•</span>
                        <Link className="font-medium text-accent hover:underline" to="/sms-opt-in-proof">
                          {t("smsConsentPage.smsOptInProofLinkLabel")}
                        </Link>
                      </div>
                    </div>
                  ) : null}
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PublicSiteLayout>
  );
}
